import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import SearchableSelect from "@/components/shared/SearchableSelect";
import {
  Boxes, KeyRound, Wrench, LogOut, Search, Plus, Upload, X, Trash2,
  Eye, EyeOff, RefreshCw, Package, ClipboardList, FileDown,
} from "lucide-react";

const ASSET_STATUSES = ["Available", "Assigned", "Repair", "Perished", "Missing", "Lost", "Stolen"];
const TICKET_STATUSES = ["Pending", "In Progress", "Completed"];
const STATUS_TONE = {
  Available: "text-emerald-400 border-emerald-500/30 bg-emerald-500/5",
  Assigned:  "text-[#d4af37] border-[#d4af37]/30 bg-[#d4af37]/5",
  Repair:    "text-amber-400 border-amber-500/30 bg-amber-500/5",
  Perished:  "text-gray-500 border-gray-600/30 bg-gray-800/20",
  Missing:   "text-orange-400 border-orange-500/30 bg-orange-500/5",
  Lost:      "text-red-400 border-red-500/30 bg-red-500/5",
  Stolen:    "text-red-500 border-red-500/40 bg-red-500/10",
};

// Feb 2026 · fallback branch list — mirrors backend /hr/v2/lookups/branches
// so a network hiccup during form open still leaves the picker usable.
const FALLBACK_BRANCHES = [
  { code: "kasturinagar", label: "Kasturinagar Branch" },
  { code: "sarjapur",     label: "Sarjapur Branch" },
  { code: "jp_nagar",     label: "JP Nagar Branch" },
  { code: "hoskote",      label: "Hoskote" },
  { code: "blr_exe",      label: "BLR EXE" },
  { code: "hyd_exe",      label: "HYD EXE" },
  { code: "hyderabad",    label: "Hyderabad Branch" },
];

// Cache lookups at the module level so opening multiple modals doesn't
// re-fetch. `refresh()` re-queries after a mutation that changes the pool
// (e.g. new asset added).
function useHrLookups() {
  const [employees, setEmployees] = useState([]);
  const [assets, setAssets] = useState([]);
  const [branches, setBranches] = useState(FALLBACK_BRANCHES);
  const refresh = useCallback(async () => {
    try {
      const [emp, ast, br] = await Promise.all([
        api.get("/hr/v2/lookups/employees"),
        api.get("/hr/v2/lookups/asset-ids"),
        api.get("/hr/v2/lookups/branches").catch(() => ({ data: FALLBACK_BRANCHES })),
      ]);
      setEmployees(emp.data); setAssets(ast.data); setBranches(br.data);
    } catch { /* fail-silent — fallback branches still render */ }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  const empOptions = useMemo(() => employees.map((u) => ({
    value: u.employee_id, label: `${u.name} · ${u.employee_id}`, sub: u.email,
  })), [employees]);
  const assetOptions = useMemo(() => assets.map((a) => ({
    value: a.asset_id,
    label: `${a.asset_id} · ${a.category}${a.brand ? ` · ${a.brand}` : ""}`,
    sub: `${a.current_status}${a.employee_id ? ` · ${a.employee_id}` : ""}`,
  })), [assets]);
  const availableAssetOptions = useMemo(
    () => assetOptions.filter((_, i) => assets[i].current_status === "Available"),
    [assetOptions, assets],
  );
  const branchOptions = useMemo(() => branches.map((b) => ({
    value: b.label, label: b.label,
  })), [branches]);
  return { empOptions, assetOptions, availableAssetOptions, branchOptions, refresh };
}

export default function HrAssetsHubPage() {
  const [tab, setTab] = useState("inventory");
  const [summary, setSummary] = useState({ total: 0, by_status: {}, open_tickets: 0 });
  const lookups = useHrLookups();

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/hr/v2/master-assets/summary");
      setSummary(data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  const bounce = () => { loadSummary(); lookups.refresh(); };

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-6 bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">HR · Asset Module</p>
        <h1 className="font-display text-3xl text-white mt-1">Asset Command Center</h1>
        <p className="text-xs text-gray-500 mt-1">Inventory, allocation, credentials & maintenance — one console.</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 px-10 pt-6" data-testid="hr-asset-summary">
        <KpiCard label="Total Inventory" value={summary.total} icon={Package} />
        <KpiCard label="Available"       value={summary.by_status?.Available || 0} tone="emerald" />
        <KpiCard label="Assigned"        value={summary.by_status?.Assigned || 0}  tone="gold" />
        <KpiCard label="In Repair"       value={summary.by_status?.Repair || 0}    tone="amber" />
        <KpiCard label="Open Tickets"    value={summary.open_tickets} icon={ClipboardList} tone="gold" />
      </div>

      {/* Tab strip */}
      <div className="px-10 pt-6">
        <div className="flex items-center gap-6 border-b border-[#1a1a1a] text-xs font-mono uppercase tracking-widest">
          {[
            { key: "inventory",   label: "Inventory Registry",  icon: Boxes },
            { key: "allocation",  label: "Onboarding Allocation", icon: Package },
            { key: "vault",       label: "Credential Vault",    icon: KeyRound },
            { key: "maintenance", label: "Maintenance Desk",    icon: Wrench },
            { key: "offboard",    label: "Offboarding Clearance", icon: LogOut },
          ].map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
                    className={`flex items-center gap-2 py-3 -mb-px border-b-2 transition-colors ${
                      tab === t.key ? "border-[#d4af37] text-[#d4af37]" : "border-transparent text-gray-500 hover:text-white"
                    }`}
                    data-testid={`hr-asset-tab-${t.key}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-10 py-6 space-y-6">
        {tab === "inventory"   && <InventoryTab lookups={lookups} onChanged={bounce} />}
        {tab === "allocation"  && <AllocationTab lookups={lookups} onChanged={bounce} />}
        {tab === "vault"       && <VaultTab lookups={lookups} />}
        {tab === "maintenance" && <MaintenanceTab lookups={lookups} onChanged={bounce} />}
        {tab === "offboard"    && <OffboardTab onChanged={bounce} />}
      </div>
    </div>
  );
}

// ─────────────────────────────── Bits ───────────────────────────────────

function KpiCard({ label, value, icon: Icon = Boxes, tone = "neutral" }) {
  const toneCls = {
    emerald: "text-emerald-400", amber: "text-amber-400",
    gold:    "text-[#d4af37]",   neutral: "text-white",
  }[tone] || "text-white";
  return (
    <div className="iz-card p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">{label}</p>
        <Icon className="w-3.5 h-3.5 text-gray-600" />
      </div>
      <p className={`text-2xl font-display ${toneCls}`}>{value}</p>
    </div>
  );
}

function StatusChip({ status }) {
  const cls = STATUS_TONE[status] || "text-gray-400 border-gray-600/30";
  return <span className={`text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${cls}`}>{status}</span>;
}

// ─────────────────────────── Inventory Tab ────────────────────────────

function InventoryTab({ lookups, onChanged }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (statusFilter) params.set("status", statusFilter);
    try {
      const { data } = await api.get(`/hr/v2/master-assets?${params}`);
      setRows(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [q, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Feb 2026 · Two-step CSV import — user uploads → we call /preview →
  // render the plan in a modal → on Confirm we call /import with the SAME
  // file so HR spots dup-serial / missing-columns issues before polluting
  // the master.
  const openPreview = async (file) => {
    if (!file) return;
    setImporting(true); setPreviewFile(file);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/hr/v2/master-assets/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreviewData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setPreviewFile(null);
    } finally { setImporting(false); }
  };

  const confirmImport = async (rows) => {
    // Feb 2026 · Commit path — send the (possibly-edited) rows as JSON so
    // HR's inline fixes in the preview modal actually stick without a
    // re-upload cycle.
    if (!rows?.length) return;
    setImporting(true);
    try {
      const { data } = await api.post("/hr/v2/master-assets/import/commit", { rows });
      toast.success(`Imported ${data.created} · Skipped ${data.skipped.length}`);
      if (data.skipped.length) {
        // Show which fixes still failed so HR can adjust and retry.
        toast.warning(`${data.skipped.length} row(s) still failed — see console`);
        // eslint-disable-next-line no-console
        console.warn("Skipped after commit:", data.skipped);
      }
      setPreviewData(null); setPreviewFile(null); load(); onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search asset id, serial, brand, employee…"
                   className="iz-input pl-9 min-w-[320px]" data-testid="hr-asset-search" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="iz-input" data-testid="hr-asset-status-filter">
            <option value="">All statuses</option>
            {ASSET_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              // Feb 2026 · downloadable CSV template — HR gets the exact
              // column names the /import endpoint expects, no guessing.
              const csv = "category,serial_no,brand,model,purchase_date,purchase_cost,warranty_status,warranty_till,branch,location\nLaptop,SN-EXAMPLE-1,Apple,MBP 14,2025-01-15,180000,Under Warranty,2028-01-14,Kasturinagar Branch,3rd Floor\n";
              const blob = new Blob([csv], { type: "text/csv" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url; a.download = "asset-template.csv"; a.click();
              URL.revokeObjectURL(url);
            }}
            className="iz-btn-secondary text-xs flex items-center gap-1.5"
            data-testid="hr-asset-csv-template"
          >
            <FileDown className="w-3.5 h-3.5"/> Template
          </button>
          <label className="iz-btn-secondary text-xs flex items-center gap-1.5 cursor-pointer" data-testid="hr-asset-import">
            <Upload className="w-3.5 h-3.5" /> {importing ? "Reading…" : "Bulk CSV"}
            <input type="file" hidden accept=".csv"
                   onChange={(e) => { openPreview(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
          <button onClick={() => setShowCreate(true)} className="iz-btn-primary text-xs flex items-center gap-1.5" data-testid="hr-asset-add">
            <Plus className="w-3.5 h-3.5" /> Add Asset
          </button>
        </div>
      </div>

      <div className="iz-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1000px]">
            <thead>
              <tr className="text-[9px] font-mono uppercase tracking-widest text-gray-500 border-b border-[#1a1a1a]">
                <th className="text-left px-4 py-3">Asset ID</th>
                <th className="text-left px-4 py-3">Category · Brand · Model</th>
                <th className="text-left px-4 py-3">Serial</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Employee</th>
                <th className="text-left px-4 py-3">Branch · Location</th>
                <th className="text-left px-4 py-3">Warranty</th>
                <th className="text-left px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-xs font-mono text-gray-500">No assets — click Add Asset to get started.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[#0f0f0f]" data-testid={`hr-asset-row-${r.asset_id}`}>
                  <td className="px-4 py-3 text-white font-mono text-xs">{r.asset_id}</td>
                  <td className="px-4 py-3 text-gray-300 text-xs">{r.category} · {r.brand || "—"} · {r.model || "—"}</td>
                  <td className="px-4 py-3 text-gray-500 font-mono text-xs">{r.serial_no}</td>
                  <td className="px-4 py-3"><StatusChip status={r.current_status} /></td>
                  <td className="px-4 py-3 text-xs">
                    {r.employee_name ? <span className="text-gray-200">{r.employee_name}<br/><span className="font-mono text-[10px] text-gray-500">{r.employee_id}</span></span>
                                      : <span className="italic text-gray-600 text-[10px]">Unallocated</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">{r.branch || "—"} · {r.location || "—"}</td>
                  <td className="px-4 py-3 text-xs">
                    {r.warranty_status ? <span className={r.warranty_status === "Expired" ? "text-red-400" : "text-emerald-400"}>{r.warranty_status}</span> : <span className="text-gray-600">—</span>}
                    {r.warranty_till && <div className="text-[10px] text-gray-500 font-mono">till {r.warranty_till}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => setEditing(r)} className="text-[#d4af37] hover:underline text-[10px] font-mono uppercase" data-testid={`hr-asset-edit-${r.asset_id}`}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && <AssetFormModal branchOptions={lookups.branchOptions} onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); onChanged?.(); }} />}
      {editing && <AssetFormModal branchOptions={lookups.branchOptions} asset={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); onChanged?.(); }} />}
      {previewData && (
        <ImportPreviewModal
          data={previewData}
          filename={previewFile?.name}
          confirming={importing}
          branchOptions={lookups.branchOptions}
          onCancel={() => { setPreviewData(null); setPreviewFile(null); }}
          onConfirm={confirmImport}
        />
      )}
    </div>
  );
}

function ImportPreviewModal({ data, filename, confirming, branchOptions = [], onCancel, onConfirm }) {
  // Feb 2026 · Inline row editing. HR can fix skipped rows right here —
  // typing into any input toggles the row from "skipped" to "will retry".
  // On Confirm we send the merged set (validated + edited-and-retried) as
  // JSON to /import/commit, no CSV re-upload needed.
  const FIELDS = [
    { key: "category",       label: "Category*",  wide: false },
    { key: "serial_no",      label: "Serial No*", wide: false },
    { key: "brand",          label: "Brand",      wide: false },
    { key: "model",          label: "Model",      wide: false },
    { key: "purchase_date",  label: "Purchase Date", wide: false, type: "date" },
    { key: "purchase_cost",  label: "Cost",       wide: false, type: "number" },
    { key: "warranty_status",label: "Warranty",   wide: false },
    { key: "warranty_till",  label: "Warranty Till", wide: false, type: "date" },
    { key: "branch",         label: "Branch",     wide: true },
    { key: "location",       label: "Location",   wide: false },
  ];
  const seed = (r) => Object.fromEntries(FIELDS.map((f) => [f.key, r[f.key] ?? ""]));

  // Editable buffers. Skipped rows can also be marked "drop" to exclude
  // from the commit if HR decides not to fix them.
  const [createdRows, setCreatedRows] = useState(() => data.created.map(seed));
  const [skippedRows, setSkippedRows] = useState(() => data.skipped.map((r) => ({
    ...seed(r), _reason: r.reason, _dropped: false, _edited: false,
  })));

  const patchSkip = (i, key, value) => setSkippedRows((rows) => rows.map((r, idx) =>
    idx === i ? { ...r, [key]: value, _edited: true } : r,
  ));
  const dropSkip = (i) => setSkippedRows((rows) => rows.map((r, idx) =>
    idx === i ? { ...r, _dropped: !r._dropped } : r,
  ));

  const editedRetryCount = skippedRows.filter((r) => r._edited && !r._dropped).length;
  const willCommitCount = createdRows.length + editedRetryCount;
  const nothingToDo = willCommitCount === 0;

  const handleConfirm = () => {
    const toSend = [
      ...createdRows.map((r) => stripEmpty(r)),
      ...skippedRows
        .filter((r) => r._edited && !r._dropped)
        .map((r) => stripEmpty(r)),
    ];
    onConfirm(toSend);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onCancel}>
      <div className="w-full max-w-6xl iz-card p-8 relative max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="hr-asset-import-preview">
        <button onClick={onCancel} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500" data-testid="preview-close"><X className="w-4 h-4"/></button>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-2">Import Preview · {filename}</p>
        <h2 className="font-display text-2xl text-white mb-1">Review & fix before commit</h2>
        <p className="text-xs text-gray-500 mb-6">
          Nothing has been written yet. Edit any skipped row inline to retry it, or drop it to exclude.
          Confirm will commit <span className="text-[#d4af37]">{willCommitCount}</span> row(s).
        </p>

        {/* Will Create block */}
        <section data-testid="preview-created" className="mb-6">
          <p className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 mb-2">
            Will Create · {createdRows.length}
          </p>
          <div className="border border-emerald-500/20 rounded max-h-72 overflow-y-auto">
            {createdRows.length === 0 && (
              <p className="p-3 text-xs font-mono text-gray-600 italic">Nothing to create from this file.</p>
            )}
            {createdRows.length > 0 && (
              <table className="w-full text-xs">
                <thead className="bg-emerald-500/5 text-[10px] uppercase font-mono tracking-widest text-emerald-400/80">
                  <tr>
                    <th className="text-left px-3 py-1.5">Row</th>
                    <th className="text-left px-3 py-1.5">Category</th>
                    <th className="text-left px-3 py-1.5">Serial</th>
                    <th className="text-left px-3 py-1.5">Brand · Model</th>
                    <th className="text-left px-3 py-1.5">Branch</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#0f0f0f]">
                  {data.created.map((r, i) => (
                    <tr key={r.row}>
                      <td className="px-3 py-2 font-mono text-[10px] text-gray-500">{r.row}</td>
                      <td className="px-3 py-2 text-white">{r.category}</td>
                      <td className="px-3 py-2 font-mono text-gray-300">{r.serial_no}</td>
                      <td className="px-3 py-2 text-gray-400">{[r.brand, r.model].filter(Boolean).join(" · ") || "—"}</td>
                      <td className="px-3 py-2 text-gray-400 truncate max-w-[10rem]">{r.branch || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* Skipped / fixable block */}
        <section data-testid="preview-skipped">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-mono uppercase tracking-widest text-red-400">
              Needs Fixing · {skippedRows.filter((r) => !r._dropped).length}
              {editedRetryCount > 0 && <span className="text-emerald-400 ml-2">· {editedRetryCount} ready to retry</span>}
            </p>
          </div>
          <div className="border border-red-500/20 rounded max-h-[26rem] overflow-y-auto divide-y divide-[#0f0f0f]">
            {skippedRows.length === 0 && (
              <p className="p-3 text-xs font-mono text-gray-600 italic">No skips. Every row is valid.</p>
            )}
            {skippedRows.map((r, i) => (
              <div key={i} className={`p-3 space-y-2 ${r._dropped ? "opacity-40" : ""}`}>
                <div className="flex items-center justify-between text-[10px] font-mono">
                  <span className="text-gray-500">Row {data.skipped[i].row}</span>
                  <span className="text-red-400 flex-1 px-3 truncate">{r._reason}</span>
                  {r._edited && !r._dropped && (
                    <span className="text-emerald-400 uppercase tracking-widest mr-3">edited · will retry</span>
                  )}
                  <button onClick={() => dropSkip(i)}
                          className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${
                            r._dropped ? "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                                       : "border-red-500/30 text-red-400 hover:bg-red-500/10"
                          }`}
                          data-testid={`preview-skip-drop-${i}`}>
                    {r._dropped ? "Undo drop" : "Drop"}
                  </button>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {FIELDS.map((f) => (
                    <label key={f.key} className={`text-[10px] font-mono uppercase tracking-widest text-gray-500 ${f.wide ? "col-span-2" : ""}`}>
                      {f.label}
                      <input value={r[f.key] || ""} type={f.type || "text"}
                             disabled={r._dropped}
                             onChange={(e) => patchSkip(i, f.key, e.target.value)}
                             className="mt-1 w-full bg-[#0a0a0a] border border-[#1c1c1c] focus:border-[#d4af37]/60 rounded px-2 py-1 text-xs text-white font-normal disabled:cursor-not-allowed"
                             list={f.key === "branch" ? "hr-branch-list" : undefined}
                             data-testid={`preview-skip-input-${i}-${f.key}`}/>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <datalist id="hr-branch-list">
            {branchOptions.map((b) => <option key={b.value} value={b.value} />)}
          </datalist>
        </section>

        <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-[#1a1a1a]">
          <button onClick={onCancel} className="iz-btn-secondary" data-testid="preview-cancel">
            Cancel — nothing written
          </button>
          <button onClick={handleConfirm}
                  disabled={confirming || nothingToDo}
                  className="iz-btn-primary flex items-center gap-1.5"
                  data-testid="preview-confirm"
                  title={nothingToDo ? "Nothing to commit — fix or drop skipped rows" : undefined}>
            {confirming ? "Committing…" : `Commit ${willCommitCount} row(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Strip UI-only helper flags and empty strings so the JSON commit payload
// matches the ImportRowPayload contract exactly.
function stripEmpty(row) {
  const out = {};
  for (const [k, v] of Object.entries(row)) {
    if (k.startsWith("_")) continue;
    if (v === "" || v === undefined || v === null) continue;
    out[k] = v;
  }
  return out;
}

// Sentinel placeholder — original JSX resumes below via the anchor.
function _InventoryTabTailAnchor__() { return null; }

function AssetFormModal({ asset, branchOptions = [], onClose, onSaved }) {
  const isEdit = !!asset;
  const [form, setForm] = useState({
    category: asset?.category || "Laptop", serial_no: asset?.serial_no || "",
    brand: asset?.brand || "", model: asset?.model || "",
    purchase_date: asset?.purchase_date || "", purchase_cost: asset?.purchase_cost ?? "",
    warranty_status: asset?.warranty_status || "", warranty_till: asset?.warranty_till || "",
    branch: asset?.branch || "", location: asset?.location || "",
    current_status: asset?.current_status || "Available",
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form };
      if (payload.purchase_cost === "") delete payload.purchase_cost; else payload.purchase_cost = Number(payload.purchase_cost);
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      if (isEdit) { await api.patch(`/hr/v2/master-assets/${asset.asset_id}`, payload); toast.success("Asset updated"); }
      else        { await api.post("/hr/v2/master-assets", payload); toast.success("Asset added"); }
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-2xl iz-card p-8 relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500"><X className="w-4 h-4"/></button>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-2">{isEdit ? "Edit" : "New"} Asset</p>
        <h2 className="font-display text-2xl text-white mb-6">{isEdit ? asset.asset_id : "Register a new asset"}</h2>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <Field label="Category" required><input value={form.category} onChange={(e) => setForm(f => ({...f, category: e.target.value}))} className="iz-input" required data-testid="hr-asset-field-category" placeholder="Laptop / Mobile / Monitor / …"/></Field>
          <Field label="Serial No" required><input value={form.serial_no} onChange={(e) => setForm(f => ({...f, serial_no: e.target.value}))} className="iz-input" required disabled={isEdit} data-testid="hr-asset-field-serial"/></Field>
          <Field label="Brand"><input value={form.brand} onChange={(e) => setForm(f => ({...f, brand: e.target.value}))} className="iz-input"/></Field>
          <Field label="Model"><input value={form.model} onChange={(e) => setForm(f => ({...f, model: e.target.value}))} className="iz-input"/></Field>
          <Field label="Purchase Date"><input type="date" value={form.purchase_date} onChange={(e) => setForm(f => ({...f, purchase_date: e.target.value}))} className="iz-input"/></Field>
          <Field label="Purchase Cost (₹)"><input type="number" step="0.01" value={form.purchase_cost} onChange={(e) => setForm(f => ({...f, purchase_cost: e.target.value}))} className="iz-input"/></Field>
          <Field label="Warranty Status">
            <select value={form.warranty_status} onChange={(e) => setForm(f => ({...f, warranty_status: e.target.value}))} className="iz-input">
              <option value="">—</option><option>Under Warranty</option><option>Expired</option>
            </select>
          </Field>
          <Field label="Warranty Till"><input type="date" value={form.warranty_till} onChange={(e) => setForm(f => ({...f, warranty_till: e.target.value}))} className="iz-input"/></Field>
          <Field label="Branch">
            <SearchableSelect
              value={form.branch}
              onChange={(v) => setForm(f => ({...f, branch: v}))}
              options={branchOptions}
              placeholder="Select branch…"
              testid="hr-asset-field-branch"
            />
          </Field>
          <Field label="Location"><input value={form.location} onChange={(e) => setForm(f => ({...f, location: e.target.value}))} className="iz-input"/></Field>
          {isEdit && (
            <Field label="Status">
              <select value={form.current_status} onChange={(e) => setForm(f => ({...f, current_status: e.target.value}))} className="iz-input">
                {ASSET_STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </Field>
          )}
          <div className="col-span-2 flex justify-end gap-3 pt-4 border-t border-[#1a1a1a]">
            <button type="button" onClick={onClose} className="iz-btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="iz-btn-primary" data-testid="hr-asset-save">{saving ? "Saving…" : (isEdit ? "Save" : "Create")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">
        {label} {required && <span className="text-[#d4af37]">*</span>}
      </label>
      {children}
    </div>
  );
}

// ─────────────────────────── Allocation Tab ───────────────────────────

function AllocationTab({ lookups, onChanged }) {
  const [empId, setEmpId] = useState("");
  const [selected, setSelected] = useState("");
  const allocate = async () => {
    if (!selected || !empId.trim()) { toast.error("Pick an asset and Employee ID"); return; }
    try {
      await api.post(`/hr/v2/master-assets/${selected}/allocate`, { employee_id: empId.trim() });
      toast.success("Asset allocated"); setEmpId(""); setSelected(""); onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-6">
      <div className="iz-card p-6 grid grid-cols-3 gap-4 items-end">
        <Field label="Available Asset" required>
          <SearchableSelect
            value={selected} onChange={setSelected}
            options={lookups.availableAssetOptions}
            placeholder="Pick an Available asset…"
            testid="allocate-asset-select"
          />
        </Field>
        <Field label="Employee ID" required>
          <SearchableSelect
            value={empId} onChange={setEmpId}
            options={lookups.empOptions}
            placeholder="Pick an employee…"
            testid="allocate-emp-id"
          />
        </Field>
        <button onClick={allocate} className="iz-btn-primary" data-testid="allocate-submit">Allocate</button>
      </div>
      <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
        {lookups.availableAssetOptions.length} available asset(s)
      </p>
    </div>
  );
}

// ────────────────────────────── Vault Tab ─────────────────────────────

function VaultTab({ lookups }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [reveal, setReveal] = useState({});
  const [historyCred, setHistoryCred] = useState(null);
  const load = async () => {
    try { const { data } = await api.get(`/hr/v2/credentials${q ? `?q=${encodeURIComponent(q)}` : ""}`); setRows(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);
  const del = async (id) => {
    if (!window.confirm("Delete this credential record?")) return;
    try { await api.delete(`/hr/v2/credentials/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employee or asset id…" className="iz-input max-w-md" data-testid="vault-search"/>
        <button onClick={() => setShowForm(true)} className="iz-btn-primary text-xs flex items-center gap-1.5" data-testid="vault-add"><Plus className="w-3.5 h-3.5"/> New Record</button>
      </div>
      <div className="space-y-3">
        {rows.length === 0 && <p className="text-xs text-gray-500 italic">No credential records yet.</p>}
        {rows.map((r) => (
          <div key={r.id} className="iz-card p-4" data-testid={`vault-card-${r.id}`}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm text-white">{r.employee_name || r.employee_id}</p>
                <p className="text-[10px] font-mono text-gray-500">{r.employee_id} · asset {r.asset_id}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setHistoryCred(r)}
                        className="text-[10px] font-mono uppercase tracking-widest text-gray-400 hover:text-[#d4af37] px-2"
                        data-testid={`vault-history-${r.id}`}>History</button>
                <button onClick={() => setReveal(s => ({...s, [r.id]: !s[r.id]}))} className="p-1.5 rounded hover:bg-[#1c1c1c] text-gray-400 hover:text-[#d4af37]" data-testid={`vault-reveal-${r.id}`}>
                  {reveal[r.id] ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                </button>
                <button onClick={() => del(r.id)} className="p-1.5 rounded hover:bg-red-500/10 text-gray-400 hover:text-red-400" data-testid={`vault-del-${r.id}`}><Trash2 className="w-4 h-4"/></button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              {["gmail","zoho","laptop","mobile","apple"].map((k) => (
                (r[`${k}_username`] || r[`${k}_password`]) && (
                  <div key={k} className="border border-[#1a1a1a] rounded p-2">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">{k}</p>
                    <p className="text-gray-300">{r[`${k}_username`] || "—"}</p>
                    <p className="text-gray-500 font-mono">{reveal[r.id] ? (r[`${k}_password`] || "—") : "••••••••"}</p>
                  </div>
                )
              ))}
            </div>
          </div>
        ))}
      </div>
      {showForm && <CredentialFormModal lookups={lookups} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }}/>}
      {historyCred && <AuditHistoryDrawer cred={historyCred} onClose={() => setHistoryCred(null)}/>}
    </div>
  );
}

function AuditHistoryDrawer({ cred, onClose }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/hr/v2/credentials/${cred.id}/audit`); setRows(data); }
      catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setRows([]); }
    })();
  }, [cred.id]);
  // Feb 2026 · full-text filter — matches actor name, role, changed field
  // names, and the (masked) before/after values so a monthly compliance
  // export narrows to just the rows the auditor cares about.
  const filtered = useMemo(() => {
    if (!rows) return null;
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => {
      const hay = [
        r.changed_by_name, r.changed_by_role, r.timestamp,
        ...(r.changes || []).flatMap((c) => [c.field, c.before, c.after]),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, q]);
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-lg h-full bg-[#0a0a0a] border-l border-[#262626] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0a0a0a] border-b border-[#1a1a1a] p-5 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Credential Audit</p>
            <h3 className="font-display text-xl text-white">{cred.asset_id}</h3>
            <p className="text-[10px] font-mono text-gray-500 mt-0.5">{cred.employee_name || cred.employee_id}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-5 pb-3">
          <div className="relative mb-4">
            <Search className="w-3.5 h-3.5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2"/>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter by actor, field, value…"
              className="iz-input pl-9 w-full text-xs"
              data-testid="audit-search"
            />
          </div>
          {rows === null && <p className="text-xs font-mono text-gray-500">Loading…</p>}
          {rows && filtered.length === 0 && (
            <p className="text-xs font-mono text-gray-500 italic">
              {q ? "No entries match this filter." : "No edit history yet."}
            </p>
          )}
          <ul className="space-y-4" data-testid="audit-log-list">
            {filtered && filtered.map((r) => (
              <li key={r.id} className="border-l-2 border-[#d4af37]/40 pl-4 pb-1">
                <p className="text-xs text-white">
                  {r.changed_by_name}
                  <span className="text-[10px] font-mono text-gray-500 ml-2">· {r.changed_by_role}</span>
                </p>
                <p className="text-[10px] font-mono text-gray-500 mb-2">{new Date(r.timestamp).toLocaleString()}</p>
                <div className="space-y-1">
                  {r.changes.map((c, i) => (
                    <div key={i} className="text-[11px] font-mono flex items-baseline gap-2">
                      <span className="text-gray-500 w-32 flex-shrink-0 truncate">{c.field}</span>
                      <span className="text-red-400/70 line-through">{c.before || "∅"}</span>
                      <span className="text-gray-600">→</span>
                      <span className="text-emerald-400">{c.after || "∅"}</span>
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ul>
          {rows && filtered && q && (
            <p className="text-[10px] font-mono text-gray-500 text-center mt-4">
              {filtered.length} of {rows.length} match
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function CredentialFormModal({ lookups, onClose, onSaved }) {
  const [form, setForm] = useState({
    employee_id: "", asset_id: "",
    gmail_username: "", gmail_password: "",
    zoho_username: "", zoho_password: "",
    laptop_username: "", laptop_password: "",
    mobile_username: "", mobile_password: "",
    apple_username: "", apple_password: "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post("/hr/v2/credentials", payload);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-3xl iz-card p-8 relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500"><X className="w-4 h-4"/></button>
        <h2 className="font-display text-2xl text-white mb-6">New credential record</h2>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <Field label="Employee ID" required>
            <SearchableSelect
              value={form.employee_id} onChange={(v) => setForm(f => ({...f, employee_id: v}))}
              options={lookups?.empOptions || []}
              placeholder="Pick an employee…"
              testid="vault-form-emp"
              required
            />
          </Field>
          <Field label="Asset ID" required>
            <SearchableSelect
              value={form.asset_id} onChange={(v) => setForm(f => ({...f, asset_id: v}))}
              options={lookups?.assetOptions || []}
              placeholder="Pick an asset (INT_LAP_… / INT_MOB_…)"
              testid="vault-form-asset"
              required
            />
          </Field>
          {["gmail","zoho","laptop","mobile","apple"].map((k) => (
            <React.Fragment key={k}>
              <Field label={`${k[0].toUpperCase()+k.slice(1)} Username`}><input value={form[`${k}_username`]} onChange={(e) => setForm(f => ({...f, [`${k}_username`]: e.target.value}))} className="iz-input"/></Field>
              <Field label={`${k[0].toUpperCase()+k.slice(1)} Password`}><input value={form[`${k}_password`]} onChange={(e) => setForm(f => ({...f, [`${k}_password`]: e.target.value}))} className="iz-input"/></Field>
            </React.Fragment>
          ))}
          <div className="col-span-2 flex justify-end gap-3 pt-4 border-t border-[#1a1a1a]">
            <button type="button" onClick={onClose} className="iz-btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="iz-btn-primary" data-testid="vault-form-save">{saving ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ────────────────────────── Maintenance Tab ───────────────────────────

function MaintenanceTab({ lookups, onChanged }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const load = async () => {
    try { const { data } = await api.get(`/hr/v2/tickets${status ? `?status=${status}` : ""}`); setRows(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  const update = async (t, next) => {
    try { await api.patch(`/hr/v2/tickets/${t.id}`, { ticket_status: next }); toast.success(`Marked ${next}`); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="iz-input max-w-xs">
          <option value="">All statuses</option>
          {TICKET_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <button onClick={() => setShowForm(true)} className="iz-btn-primary text-xs flex items-center gap-1.5" data-testid="ticket-new"><Plus className="w-3.5 h-3.5"/> New Ticket</button>
      </div>
      <div className="iz-card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-[9px] font-mono uppercase tracking-widest text-gray-500 border-b border-[#1a1a1a]">
            <th className="text-left px-4 py-3">Request ID</th><th className="text-left px-4 py-3">Asset · Emp</th>
            <th className="text-left px-4 py-3">Service</th><th className="text-left px-4 py-3">Status</th>
            <th className="text-left px-4 py-3">Actions</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-xs text-gray-500">No tickets.</td></tr>}
            {rows.map((t) => (
              <tr key={t.id} className="border-b border-[#0f0f0f]" data-testid={`ticket-row-${t.request_id}`}>
                <td className="px-4 py-3 text-white font-mono text-xs">{t.request_id}</td>
                <td className="px-4 py-3 text-xs text-gray-300">{t.asset_id}<br/><span className="text-[10px] text-gray-500 font-mono">{t.employee_name || t.emp_id}</span></td>
                <td className="px-4 py-3 text-xs text-gray-400">{t.service_type}<br/><span className="text-[10px] text-gray-500 italic">{t.description || "—"}</span></td>
                <td className="px-4 py-3"><span className={`text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${
                  t.ticket_status === "Completed" ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/5"
                  : t.ticket_status === "In Progress" ? "text-amber-400 border-amber-500/30 bg-amber-500/5"
                  : "text-gray-400 border-gray-600/30"
                }`}>{t.ticket_status}</span></td>
                <td className="px-4 py-3 flex gap-2 text-[10px] font-mono uppercase">
                  {t.ticket_status !== "In Progress" && <button onClick={() => update(t, "In Progress")} className="text-amber-400 hover:underline">Start</button>}
                  {t.ticket_status !== "Completed" && <button onClick={() => update(t, "Completed")} className="text-emerald-400 hover:underline">Complete</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showForm && <TicketFormModal lookups={lookups} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); onChanged?.(); }}/>}
    </div>
  );
}

function TicketFormModal({ lookups, onClose, onSaved }) {
  const [form, setForm] = useState({ emp_id: "", asset_id: "", service_type: "Repair", description: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post("/hr/v2/tickets", form); toast.success("Ticket raised"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-lg iz-card p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500"><X className="w-4 h-4"/></button>
        <h2 className="font-display text-2xl text-white mb-6">Raise Maintenance Ticket</h2>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Employee ID" required>
            <SearchableSelect
              value={form.emp_id} onChange={(v) => setForm(f => ({...f, emp_id: v}))}
              options={lookups?.empOptions || []}
              placeholder="Pick an employee…"
              testid="ticket-emp"
              required
            />
          </Field>
          <Field label="Asset ID" required>
            <SearchableSelect
              value={form.asset_id} onChange={(v) => setForm(f => ({...f, asset_id: v}))}
              options={lookups?.assetOptions || []}
              placeholder="Pick an asset…"
              testid="ticket-asset"
              required
            />
          </Field>
          <Field label="Service Type" required><input value={form.service_type} onChange={(e) => setForm(f => ({...f, service_type: e.target.value}))} className="iz-input" required data-testid="ticket-service"/></Field>
          <Field label="Description"><textarea value={form.description} onChange={(e) => setForm(f => ({...f, description: e.target.value}))} className="iz-input" rows={3}/></Field>
          <div className="flex justify-end gap-3 pt-4 border-t border-[#1a1a1a]">
            <button type="button" onClick={onClose} className="iz-btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="iz-btn-primary" data-testid="ticket-save">{saving ? "Saving…" : "Raise"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ────────────────────────── Offboarding Tab ───────────────────────────

function OffboardTab({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const load = async () => {
    try { const { data } = await api.get(`/hr/v2/master-assets?status=Assigned${q ? `&q=${encodeURIComponent(q)}` : ""}`); setRows(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);
  const returnAsset = async (r) => {
    if (!window.confirm(`Return ${r.asset_id} from ${r.employee_name || r.employee_id}?\nThis wipes the credential record.`)) return;
    try { await api.post(`/hr/v2/master-assets/${r.asset_id}/return`); toast.success("Asset returned · credentials wiped"); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by employee or asset id…" className="iz-input max-w-md mb-4" data-testid="offboard-search"/>
      <div className="iz-card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-[9px] font-mono uppercase tracking-widest text-gray-500 border-b border-[#1a1a1a]">
            <th className="text-left px-4 py-3">Asset</th><th className="text-left px-4 py-3">Employee</th>
            <th className="text-left px-4 py-3">Since</th><th className="text-left px-4 py-3">Actions</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={4} className="text-center py-10 text-xs text-gray-500">No assigned assets.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-[#0f0f0f]" data-testid={`offboard-row-${r.asset_id}`}>
                <td className="px-4 py-3 text-white font-mono text-xs">{r.asset_id}<br/><span className="text-[10px] text-gray-500">{r.category} · {r.brand}</span></td>
                <td className="px-4 py-3 text-gray-300 text-xs">{r.employee_name || "—"}<br/><span className="text-[10px] font-mono text-gray-500">{r.employee_id}</span></td>
                <td className="px-4 py-3 text-[10px] font-mono text-gray-500">{new Date(r.updated_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">
                  <button onClick={() => returnAsset(r)} className="iz-btn-secondary text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5" data-testid={`offboard-return-${r.asset_id}`}>
                    <RefreshCw className="w-3 h-3"/> Mark as Returned
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
